import React, { useState, useEffect, useRef } from 'react';
import {
  FlatList,
  StyleSheet,
} from 'react-native';
import { View } from './Themed';
import Pagination from './Pagination';

interface AutoPageFlatListProps<T> {
  data: T[];
  reset_key:string,
  renderItem: ({ item, index }: { item: T, index:number }) => JSX.Element;
  horizontal?: boolean;
  time_on_page?: number; // seconds
  itemHeight?: number;   // required for vertical paging
  itemWidth?: number;    // required for horizontal paging
}

function AutoPageFlatList<T>({
  reset_key,
  data,
  renderItem,
  horizontal = false,
  time_on_page,
  itemHeight = 200,
  itemWidth = 200,
}: AutoPageFlatListProps<T>) {
  const [page, setPage] = useState(0);
  const [containerSize, setContainerSize] = useState<number>(0);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const sizePerItem = horizontal ? itemWidth : itemHeight;
  const itemsPerPage = containerSize ? Math.max(Math.floor(containerSize / sizePerItem), 1) : 1;
  const totalPages = Math.ceil(data.length / itemsPerPage);

  // Auto-advance effect
  useEffect(() => {
    if (!time_on_page || totalPages <= 1) return;

    intervalRef.current = setInterval(() => {
      setPage((prev) => (prev + 1) % totalPages);
    }, time_on_page * 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [time_on_page, totalPages]);

  useEffect(() => {
      setPage(0)
  },[reset_key])

  const onContainerLayout = (e: any) => {
    const size = horizontal ? e.nativeEvent.layout.width : e.nativeEvent.layout.height;
    setContainerSize(size);
  };

  const currentData = data.slice(page * itemsPerPage, page * itemsPerPage + itemsPerPage);

  const nextPage = () => setPage((p) => Math.min(p + 1, totalPages - 1));
  const prevPage = () => setPage((p) => Math.max(p - 1, 0));

  return (
    <View transparent style={styles.container} onLayout={onContainerLayout}>
        <View transparent style={{ flex:1 }}>
            <FlatList
                data={currentData}
                renderItem={renderItem}
                keyExtractor={(_, idx) => String(idx)}
                horizontal={horizontal}
                showsHorizontalScrollIndicator={false}
                showsVerticalScrollIndicator={false}
            />
        </View>
      <View transparent>
        <Pagination
            pages={totalPages}
            onNext={nextPage}
            onPrevious={prevPage}
            offset={page}
        />
        </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  controls: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 8,
  },
  arrow: { fontSize: 24, paddingHorizontal: 8 },
  disabled: { color: '#ccc' },
});

export default AutoPageFlatList;
